import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, SearchableSelect, StatusBadge, Tabs, useToast, type SearchableSelectOption } from '@kss/ui';
import { ChevronLeft, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import type { ApiUser, ApiUserCreate } from '@kss/api';
import { userApiService, roleService } from '@kss/api';

const FALLBACK_ROLES: SearchableSelectOption[] = [
  { value: 'admin', label: 'Admin', description: 'Full access' },
  { value: 'manager', label: 'Manager', description: 'Manage operations' },
  { value: 'staff', label: 'Staff', description: 'Day-to-day access' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only' },
];

const loadRoleOptions = async (): Promise<SearchableSelectOption[]> => {
  try {
    const rows = await roleService.all();
    if (Array.isArray(rows) && rows.length > 0) {
      return rows.map((r) => ({
        value: r.code ?? r.roleId ?? r.name,
        label: r.name,
        description: r.description,
      }));
    }
  } catch {
    /* fall through to builtin */
  }
  return FALLBACK_ROLES;
};

type TabStatus = 'default' | 'danger' | 'success' | 'warning';
type FormState = { name: string; email: string; phone: string; role: string; isActive: boolean };
const emptyForm: FormState = { name: '', email: '', phone: '', role: 'staff', isActive: true };

const STEPS = ['basic', 'contact', 'access', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  basic: 'Basic info', contact: 'Contact', access: 'Access & status', review: 'Review & save',
};
const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function UserForm() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('basic');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setLoadError(null);
        try {
          const rec = await userApiService.get(id);
          if (cancelled) return;
          setForm({
            name: rec.name ?? '', email: rec.email ?? '', phone: rec.phone ?? '',
            role: rec.role ?? 'staff', isActive: !!rec.isActive,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      } else setForm(emptyForm);
      setErrors({}); setStep('basic'); setRedTabs(new Set());
    }
    run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const validateStep = (s: StepId) => {
    const errs: typeof errors = {};
    if (s === 'basic' || s === 'review') {
      if (!form.name.trim()) errs.name = 'Name is required';
      if (!form.email.trim()) errs.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const goNext = () => {
    const i = STEPS.indexOf(step); if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const next = STEPS[i + 1]; setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; }); setStep(next);
  };
  const goPrev = () => { const i = STEPS.indexOf(step); if (i === 0) return; setRedTabs((p) => new Set(p).add(step)); setStep(STEPS[i - 1]); };
  const basicValid = !!form.name.trim() && !!form.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const handleSave = async () => {
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    setSaving(true);
    try {
      if (isEdit && id) {
        const p: ApiUser = { userId: id, name: form.name, email: form.email, phone: form.phone || undefined, role: form.role || undefined, isActive: form.isActive };
        await userApiService.update([p]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        const p: ApiUserCreate = { name: form.name, email: form.email, phone: form.phone || undefined, role: form.role || undefined, isActive: form.isActive };
        const created = await userApiService.create([p]);
        const newId = created?.[0]?.userId;
        toast.success('Saved', `${form.name} added`);
        if (newId) { navigate(`/panel/users/${newId}`, { replace: true }); } else { navigate('/panel/users'); return; }
      }
      const i = STEPS.indexOf(step);
      if (i < STEPS.length - 1) { const next = STEPS[i + 1]; setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; }); setStep(next); }
      else navigate('/panel/users');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => { const m: Record<string, TabStatus> = {}; redTabs.forEach((s) => (m[s] = 'danger')); return m; }, [redTabs]);

  const field = (label: string, key: keyof FormState, input: React.ReactNode, required?: boolean) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">{label} {required && <span className="text-danger">*</span>}</span>
      {input}
      {errors[key] && <span className="text-xs text-danger">{errors[key]}</span>}
    </label>
  );

  const basicStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Name', 'name', <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Doe" className={inputCls} />, true)}
      {field('Email', 'email', <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@company.com" className={inputCls} />, true)}
    </div>
  );
  const contactStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Phone', 'phone', <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91..." className={inputCls} />)}
    </div>
  );
  const accessStep = (
    <div className="grid gap-3 md:grid-cols-2">
      <SearchableSelect label="Role" value={form.role} onChange={(v) => set('role', v)}
        loadOptions={loadRoleOptions} placeholder="Select a role" searchPlaceholder="Search roles..." />
      {field('Status', 'isActive',
        <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => set('isActive', e.target.value === 'active')} className={selectCls}>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>)}
    </div>
  );
  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Ready to save</span></div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Name" value={form.name} />
          <Row label="Email" value={form.email} />
          <Row label="Phone" value={form.phone} />
          <Row label="Role" value={form.role} />
          <Row label="Status" value={<StatusBadge status={form.isActive ? 'active' : 'inactive'} />} />
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'basic' as const, label: STEP_LABELS.basic, content: basicStep },
    { id: 'contact' as const, label: STEP_LABELS.contact, content: contactStep },
    { id: 'access' as const, label: STEP_LABELS.access, content: accessStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <div className="mb-[3px]">
        <Tabs tabs={tabs} activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId);
            const c = STEPS.indexOf(step);
            if (t < c) setRedTabs((p) => { const n = new Set(p); for (let i = t + 1; i <= c; i++) n.add(STEPS[i]); return n; });
            else if (t > c) setRedTabs((p) => { const n = new Set(p); for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]); return n; });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus} barOnly />
      </div>
      <Card>
        <div className="min-h-[180px]">{loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}</div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            {!isLast && <Button type="button" onClick={goNext}>Next <ChevronRight className="h-4 w-4" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/users')}>Cancel</Button>
            <Button type="button" onClick={handleSave} disabled={!basicValid || saving}>
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Save & Next'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value !== '' && value != null ? value : <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
